import { useState } from 'react';
import type { User } from 'firebase/auth';
import Navigation from '../../components/Navigation';
import { ProfileSettingsProvider, useProfileSettings } from './ProfileSettingsContext';
import GeneralSettingsTab from './GeneralSettingsTab';
import AutofillTab from './AutofillTab';

interface ProfileSettingsPageProps {
  user: User;
}

function SettingsContent() {
  const { loading, message } = useProfileSettings();
  const [activeTab, setActiveTab] = useState<'general' | 'autofill'>('general');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <header className="mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">Settings</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Manage your account preferences, AI configuration, and autofill profiles.
          </p>
        </header>

        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300'
          }`}>
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {message.type === 'success' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              )}
            </svg>
            <span className="text-sm font-medium">{message.text}</span>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-8">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'general'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            General & AI
          </button>
          <button
            onClick={() => setActiveTab('autofill')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'autofill'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Autofill Profiles
          </button>
        </div>

        <div className="space-y-8">
          {activeTab === 'general' ? <GeneralSettingsTab /> : <AutofillTab />}
        </div>
      </div>
    </div>
  );
}

export default function ProfileSettingsPage({ user }: ProfileSettingsPageProps) {
  return (
    <ProfileSettingsProvider user={user}>
      <Navigation user={user} />
      <SettingsContent />
    </ProfileSettingsProvider>
  );
}
